import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { Search, Download, Trash2, Eye, Filter, Calendar, Database, BarChart3, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface CollectedData {
  id: string;
  task_id: string;
  execution_id: string;
  source_url: string;
  data_type?: string;
  raw_data: any;
  processed_data?: any;
  metadata?: any;
  hash?: string;
  is_valid: boolean;
  collected_at: string;
  created_at: string;
  scrape_tasks?: {
    id: string;
    name: string;
    target_url: string;
  };
  scrape_task_executions?: {
    id: string;
    execution_number: number;
    status: string;
    started_at: string;
  };
}

interface TaskExecution {
  id: string;
  task_id: string;
  worker_id?: string;
  execution_number: number;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  error_message?: string;
  data_collected: number;
  logs?: any;
  created_at: string;
  scrape_tasks?: {
    id: string;
    name: string;
    target_url: string;
  };
}

interface DataStatistics {
  total: number;
  valid: number;
  today: number;
  byType: { [key: string]: number };
  byTask: { [key: string]: number };
}

const ScrapeDataViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'data' | 'executions' | 'statistics'>('data');
  const [collectedData, setCollectedData] = useState<CollectedData[]>([]);
  const [executions, setExecutions] = useState<TaskExecution[]>([]);
  const [statistics, setStatistics] = useState<DataStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [taskFilter, setTaskFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [validFilter, setValidFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedData, setSelectedData] = useState<CollectedData | null>(null);
  const [showDataModal, setShowDataModal] = useState(false);

  useEffect(() => {
    if (activeTab === 'data') {
      fetchCollectedData();
    } else if (activeTab === 'executions') {
      fetchExecutions();
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab]);

  const fetchCollectedData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          page: 1,
          limit: 1000,
          data_type: typeFilter !== 'all' ? typeFilter : undefined,
          is_valid: validFilter !== 'all' ? validFilter === 'valid' : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setCollectedData(result.data.data || []);
      } else {
        console.error('获取采集数据失败:', result.error);
        toast.error('获取采集数据失败');
      }
    } catch (error) {
      console.error('获取采集数据失败:', error);
      toast.error('获取采集数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=execution_list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setExecutions(result.data.data || []);
      } else {
        console.error('获取执行记录失败:', result.error);
        toast.error('获取执行记录失败');
      }
    } catch (error) {
      console.error('获取执行记录失败:', error);
      toast.error('获取执行记录失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_statistics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setStatistics(result.data);
      } else {
        console.error('获取统计数据失败:', result.error);
        toast.error('获取统计数据失败');
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      toast.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data_type: typeFilter !== 'all' ? typeFilter : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          format: 'json'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // 创建下载链接
        const dataStr = JSON.stringify(result.data.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `scrape_data_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('数据导出成功');
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('导出数据失败:', error);
      toast.error('导出失败：' + (error as Error).message);
    }
  };

  const handleDeleteData = async (id: string) => {
    if (!confirm('确定要删除这条数据吗？')) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('数据删除成功');
      await fetchCollectedData();
    } catch (error) {
      console.error('删除数据失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!confirm('确定要清理重复数据吗？此操作不可撤销。')) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=data_cleanup`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(result.data.message);
      await fetchCollectedData();
    } catch (error) {
      console.error('清理重复数据失败:', error);
      toast.error('清理失败：' + (error as Error).message);
    }
  };

  const filteredData = collectedData.filter(data => {
    const matchesSearch = data.source_url.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (data.data_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (data.scrape_tasks?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTask = taskFilter === 'all' || data.task_id === taskFilter;
    return matchesSearch && matchesTask;
  });

  const filteredExecutions = executions.filter(execution => {
    const matchesSearch = (execution.scrape_tasks?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (execution.worker_id || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || execution.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'timeout': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}秒`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
    return `${Math.floor(seconds / 3600)}时${Math.floor((seconds % 3600) / 60)}分`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Database className="w-6 h-6 mr-2 text-blue-600" />
              采集数据查看
            </h2>
            <div className="flex items-center space-x-3">
              {activeTab === 'data' && (
                <>
                  <button
                    onClick={handleCleanupDuplicates}
                    className="flex items-center px-3 py-2 text-orange-600 border border-orange-300 rounded-md hover:bg-orange-50 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    清理重复
                  </button>
                  <button
                    onClick={handleExportData}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    导出数据
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 标签页 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('data')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'data'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              采集数据
            </button>
            <button
              onClick={() => setActiveTab('executions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'executions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              执行记录
            </button>
            <button
              onClick={() => setActiveTab('statistics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'statistics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              统计分析
            </button>
          </nav>
        </div>
        
        <div className="p-6">
          {activeTab === 'data' && (
            <>
              {/* 搜索和筛选 */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="搜索URL、数据类型或任务名称..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">全部类型</option>
                  <option value="html">HTML</option>
                  <option value="json">JSON</option>
                  <option value="text">文本</option>
                  <option value="image">图片</option>
                </select>
                <select
                  value={validFilter}
                  onChange={(e) => setValidFilter(e.target.value)}
                  className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">全部状态</option>
                  <option value="valid">有效数据</option>
                  <option value="invalid">无效数据</option>
                </select>
                <div className="flex space-x-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={fetchCollectedData}
                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Filter className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 数据列表 */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        来源URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        任务
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        类型
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        状态
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        采集时间
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((data) => (
                      <tr key={data.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-[300px] truncate" title={data.source_url}>
                            {data.source_url}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-[200px] truncate" title={data.scrape_tasks?.name || `Task ${data.task_id}`}>
                            {data.scrape_tasks?.name || `Task ${data.task_id}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {data.data_type && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {data.data_type}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            data.is_valid 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {data.is_valid ? (
                              <><CheckCircle className="w-3 h-3 mr-1" />有效</>
                            ) : (
                              <><AlertTriangle className="w-3 h-3 mr-1" />无效</>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(data.collected_at).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedData(data);
                                setShowDataModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="查看详情"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteData(data.id)}
                              className="text-red-600 hover:text-red-900"
                              title="删除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredData.length === 0 && (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">暂无采集数据</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'executions' && (
            <>
              {/* 搜索和筛选 */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="搜索任务名称或Worker ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">全部状态</option>
                  <option value="success">成功</option>
                  <option value="failed">失败</option>
                  <option value="running">运行中</option>
                  <option value="timeout">超时</option>
                </select>
              </div>

              {/* 执行记录列表 */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        任务
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        执行次数
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        状态
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        持续时间
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        数据量
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        开始时间
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredExecutions.map((execution) => (
                      <tr key={execution.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium max-w-[200px] truncate" title={execution.scrape_tasks?.name || `Task ${execution.task_id}`}>
                              {execution.scrape_tasks?.name || `Task ${execution.task_id}`}
                            </div>
                            {execution.worker_id && (
                              <div className="text-xs text-gray-500 max-w-[200px] truncate" title={`Worker: ${execution.worker_id}`}>
                                Worker: {execution.worker_id}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          #{execution.execution_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(execution.status)}`}>
                            {execution.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDuration(execution.duration_seconds)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {execution.data_collected} 条
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(execution.started_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredExecutions.length === 0 && (
                <div className="text-center py-12">
                  <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">暂无执行记录</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'statistics' && statistics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* 统计卡片 */}
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Database className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-blue-600">总数据量</p>
                    <p className="text-2xl font-bold text-blue-900">{statistics.total.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-green-600">有效数据</p>
                    <p className="text-2xl font-bold text-green-900">{statistics.valid.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-purple-600">今日采集</p>
                    <p className="text-2xl font-bold text-purple-900">{statistics.today.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-orange-600">有效率</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {statistics.total > 0 ? Math.round((statistics.valid / statistics.total) * 100) : 0}%
                    </p>
                  </div>
                </div>
              </div>

              {/* 按类型统计 */}
              {Object.keys(statistics.byType).length > 0 && (
                <div className="md:col-span-2 lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">按数据类型统计</h3>
                  <div className="space-y-3">
                    {Object.entries(statistics.byType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{type || '未分类'}</span>
                        <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 按任务统计 */}
              {Object.keys(statistics.byTask).length > 0 && (
                <div className="md:col-span-2 lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">按任务统计</h3>
                  <div className="space-y-3">
                    {Object.entries(statistics.byTask).slice(0, 10).map(([task, count]) => (
                      <div key={task} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 truncate">{task}</span>
                        <span className="text-sm font-medium text-gray-900">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 数据详情弹窗 */}
      {showDataModal && selectedData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">数据详情</h3>
                <button
                  onClick={() => setShowDataModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">来源URL</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded break-all">
                      {selectedData.source_url}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">数据类型</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedData.data_type || '未指定'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">采集时间</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {new Date(selectedData.collected_at).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">数据状态</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedData.is_valid
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedData.is_valid ? '有效' : '无效'}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedData.hash && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">数据哈希</label>
                    <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                      {selectedData.hash}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">原始数据</label>
                  <div className="bg-gray-50 p-4 rounded-md">
                    <pre className="text-xs text-gray-700 overflow-auto max-h-64">
                      {JSON.stringify(selectedData.raw_data, null, 2)}
                    </pre>
                  </div>
                </div>

                {selectedData.processed_data && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">处理后数据</label>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="text-xs text-gray-700 overflow-auto max-h-64">
                        {JSON.stringify(selectedData.processed_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {selectedData.metadata && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">元数据</label>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="text-xs text-gray-700 overflow-auto max-h-32">
                        {JSON.stringify(selectedData.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDataModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapeDataViewer;